// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
// The path math is shared with connection_line_controller.js (the drag
// preview draws the same shapes the edges do) — one copy, under lib.
// Relative import: gem JS must not use host aliases
// (app/javascript/lib/__tests__/no-host-alias-imports.test.ts).
import {
  getBezierPath as bezierPath,
  getSmoothStepPath as smoothStepPath,
  getStraightPath as straightPath
} from "../../../lib/flow-edge-paths.js"
import { handleFraction, sideAnchor } from "../../../lib/flow/utils.js"

/**
 * Edge Controller — Production-quality SVG edge rendering.
 * Ported from React Flow (xyflow/xyflow) bezier-edge.ts, smoothstep-edge.ts, straight-edge.ts,
 * MarkerDefinitions.tsx, MarkerSymbols.tsx, EdgeText.tsx, and BaseEdge.tsx.
 *
 * Supports: bezier, smoothstep, step, straight edge types.
 * Features: arrow markers (open + closed), edge labels with bg, hover/select/animated states,
 *           interaction path for wide click area, reconnect handles.
 */
export default class extends Controller {
  static values = {
    id: String,
    source: String,
    sourceHandle: { type: String, default: "right" },
    target: String,
    targetHandle: { type: String, default: "left" },
    type: { type: String, default: "bezier" },
    animated: { type: Boolean, default: false },
    label: { type: String, default: "" },
    labelStyle: { type: Object, default: {} },
    labelShowBg: { type: Boolean, default: true },
    labelBgStyle: { type: Object, default: {} },
    labelBgPadding: { type: Array, default: [6, 3] },
    labelBgBorderRadius: { type: Number, default: 4 },
    hidden: { type: Boolean, default: false },
    deletable: { type: Boolean, default: true },
    selectable: { type: Boolean, default: true },
    interactionWidth: { type: Number, default: 20 },
    reconnectable: { type: Boolean, default: true },
    markerStart: { type: String, default: "" },
    markerEnd: { type: String, default: "arrowclosed" },
    markerColor: { type: String, default: "" },
    style: { type: Object, default: {} },
    curvature: { type: Number, default: 0.25 },
    borderRadius: { type: Number, default: 5 },
    offset: { type: Number, default: 20 }
  }

  connect() {
    this.element.dataset.edge = this.idValue
    this.element.classList.add("ui-flow-edge")
    if (this.animatedValue) this.element.classList.add("animated")
    if (this.hiddenValue) this.element.style.display = "none"
    if (this.styleValue && typeof this.styleValue === "object") Object.assign(this.element.style, this.styleValue)
    // Set stroke-width as a presentation attribute so markerUnits="strokeWidth" works in all browsers
    if (!this.element.getAttribute("stroke-width")) {
      this.element.setAttribute("stroke-width", "1.5")
    }

    this._interactionPath = null
    this._labelGroup = null
    this._sourceHandle = null
    this._targetHandle = null
    this._markers = []
    // Wave-8 C4: cached label metrics (getBBox forces SVG layout) and the
    // cached parallel group ({count, index}) — both were re-derived per
    // recompute FRAME before (a nodemove drag re-scanned every edge path in
    // the svg per labeled edge). Rebuilt at connect, refreshed on edge
    // membership events and endpoint rewires, cleared on disconnect.
    this._labelMetrics = null
    this._parallelGroup = null

    // Perf state: cached endpoint elements/dims, rAF coalescing, flow lookup.
    this._els = new Map()       // nodeId → endpoint element
    this._dims = new Map()      // nodeId → { w, h } (invalidated on resize)
    this._pending = false       // one frame flush scheduled?
    this._frameRaf = null
    this._labelRaf = null       // pending label-measure frame (cancelled in disconnect)
    this._flowResolved = false
    this._flowControllerCache = null
    // Wave-8 C2: cached "an endpoint is DOM-nested inside another node"
    // (sub-flow anchor) — null = unresolved. Flat graphs settle the
    // ancestor-carries check with two string compares instead of DOM
    // resolution. Invalidated on endpoint rewire (source/target value
    // change) and on stale-element re-resolution in _resolveEndpoint.
    this._nestedAnchors = null
    // Port-qualified endpoints (conek-workflows engine): edges addressed as
    // "node.port" ("gate.vip") carry NO handle value — the split fallback in
    // _resolveEndpoint records the port suffix here per side, and _updatePath
    // feeds it to _getHandlePosition in place of sourceHandleValue /
    // targetHandleValue so the path anchors on the declared port handle's
    // side. Cleared when the full id resolves as-is or the endpoint rewires.
    this._portOverride = {}
    // Which full ids were resolved through the split (full id → port suffix).
    // The _els cache stores the PREFIX node under the full id, so a later
    // cache hit can't tell a split resolution from a plain node — this map
    // re-arms the override on the hit path (and stays empty for legitimate
    // dotted node ids, which resolve directly).
    this._portSplit = new Map()

    this._createInteractionPath()
    if (this.reconnectableValue) this._createReconnectHandles()
    this._setupMarkers()
    // Initially-hidden edge: the overlays created above are SVG siblings of
    // the path — sync them too (the label group doesn't exist yet; its
    // creation path in _updateLabel checks hiddenValue itself).
    if (this.hiddenValue) this._syncOverlayVisibility()

    // Register with the engine's element cache (the flow has no outlet on the
    // edge element, so resolve it once through the enclosing canvas).
    this._flowController()?._registerEdgeElement?.(this.idValue, this.element)

    // Filtered nodemove (perf): _updatePath only when one of THIS edge's
    // endpoints actually moved — without the filter every edge in the graph
    // recomputed per moved node per pointermove (O(S selected × E edges)).
    // Events without a nodeId detail fall through to a full recompute.
    // ROW 9: a PARENT sub-flow drag must also pass the filter — see
    // _movedAncestorCarriesEndpoint.
    this._onNodeMove = (event) => {
      const nodeId = event?.detail?.nodeId
      if (nodeId && nodeId !== this._endpointNodeId("source") && nodeId !== this._endpointNodeId("target") &&
          !this._movedAncestorCarriesEndpoint(nodeId, event)) return
      // Read phase, batched (stress_500 ~4FPS fix): warm the endpoint dims
      // cache NOW — inside the synchronous dispatch, where every edge's
      // offsetWidth/offsetHeight reads run back-to-back BEFORE any rAF write
      // (`d`, label transform, updater cx/cy). Reading them in the flush
      // instead interleaves read→write→read→write per edge and forces a
      // synchronous layout per edge per frame.
      this._primeEndpointDims()
      this._scheduleFrameUpdate()
    }
    document.addEventListener("ui--flow:nodemove", this._onNodeMove)
    // Resizing changes the node box, so endpoints go stale exactly like a
    // move. Filtered (wave-8 C1): only the resized node's own edges
    // recompute — plus, for nested graphs, edges whose endpoint the resized
    // node CARRIES (origin-moving w/n handles shift sub-flow children's
    // absolute positions — the ROW 9 ancestor rule, same as nodemove). The
    // old unfiltered clear+recompute cost one forced-layout re-read per
    // EDGE per resize pointermove (549 re-reads per frame on stress_500).
    this._onNodeResize = (event) => {
      const nodeId = event?.detail?.nodeId
      if (nodeId != null) {
        if (nodeId === this._endpointNodeId("source") || nodeId === this._endpointNodeId("target") ||
            this._movedAncestorCarriesEndpoint(nodeId, event)) {
          this._evictDimsForEndpoint(nodeId)
          this._primeEndpointDims() // read-phase batching (same idiom as _onNodeMove)
          this._scheduleFrameUpdate()
        }
        return
      }
      // Fallback for emitters without detail.nodeId: clear/recompute all.
      this._dims.clear()
      this._scheduleFrameUpdate()
    }
    document.addEventListener("ui--flow:noderesize", this._onNodeResize)
    // Live width/height patches (updateNode) stale the dims cache for THIS
    // edge's endpoints — clear that entry AND schedule the recompute; without
    // the schedule the edge kept drawing to the old box until an unrelated
    // move happened to dirty it (S11).
    this._onNodeChange = (event) => {
      const { nodeId, attrs } = event?.detail || {}
      if (nodeId !== this._endpointNodeId("source") && nodeId !== this._endpointNodeId("target")) return
      if (attrs && (attrs.width !== undefined || attrs.height !== undefined)) {
        this._evictDimsForEndpoint(nodeId)
        this._scheduleFrameUpdate()
      }
    }
    document.addEventListener("ui--flow:nodechange", this._onNodeChange)
    // M5: an edge-first sync batch (addEdge ahead of its endpoints' addNode)
    // leaves _updatePath's missing-endpoint retry loop running on borrowed
    // time — ≤10 retries over ~1.7s, then it gives up FOREVER and the edge
    // stays invisible until an unrelated nodemove. nodesadd is the signal a
    // missing endpoint just materialized: drop the accrued backoff and
    // reschedule immediately. The per-episode retry cap stays (bounded work
    // per outage); only the leak across episodes is gone.
    this._onNodesAdd = (event) => {
      const added = event?.detail?.nodes
      if (!Array.isArray(added)) return
      const hit = added.some((n) => {
        const id = typeof n === "object" && n !== null ? n.id : n
        return id === this._endpointNodeId("source") || id === this._endpointNodeId("target")
      })
      if (!hit) return
      this._retryCount = 0
      this._scheduleUpdate()
    }
    document.addEventListener("ui--flow:nodesadd", this._onNodesAdd)
    // Parallel-edge label nudge (UX audit): the group index is positional
    // (DOM order among same-pair siblings), so an edge arriving or leaving
    // re-orders the group — refresh the cached group (wave-8 C4) and re-run
    // label POSITIONING only, no path rebuild.
    this._onEdgeMembership = () => { this._refreshParallelGroup(); this._repositionLabel() }
    document.addEventListener("ui--flow:edgesadd", this._onEdgeMembership)
    document.addEventListener("ui--flow:edgedelete", this._onEdgeMembership)

    // Keyboard access (T21 parity with nodes): the visible path is focusable
    // and operable. Attributes also travel in EdgeComponent's markup so they
    // exist pre-hydration; this covers edges created at runtime by the
    // engine's addEdge stub, which carries identity attributes only.
    this.element.setAttribute("role", "button")
    this.element.setAttribute("aria-label", this._edgeAriaLabel())
    if (!this.hiddenValue) this.element.setAttribute("tabindex", "0")
    this._onEdgeKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        this.select()
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (!this.element.classList.contains("selected")) return
        e.preventDefault()
        // Deletion is the engine's whenever one is reachable (M2): document
        // keydown → _deleteSelected → removeEdge for every id in
        // selectedEdges — exactly where engine-first select() now puts this
        // edge. `ui--flow:edgerequestdelete` below is the residual path: it
        // covers ids the engine does NOT own (locally-selected edges on a
        // standalone mount, stale engine sets), and the engine's listener
        // early-returns for ids already in selectedEdges.
        // S8: dispatched on the edge ELEMENT (React Flow parity: the event
        // originates where the gesture happened); bubbling still reaches the
        // engine's document listener.
        this.element.dispatchEvent(new CustomEvent("ui--flow:edgerequestdelete", {
          detail: { edgeId: this.idValue }, bubbles: true
        }))
      }
    }
    this.element.addEventListener("keydown", this._onEdgeKeyDown)

    this._retryCount = 0
    // C4: build the parallel-group cache at connect (SSR siblings are all in
    // the svg by now; runtime siblings refresh it via the membership hook).
    this._refreshParallelGroup()
    this._scheduleUpdate()
  }

  disconnect() {
    document.removeEventListener("ui--flow:nodemove", this._onNodeMove)
    document.removeEventListener("ui--flow:noderesize", this._onNodeResize)
    document.removeEventListener("ui--flow:nodechange", this._onNodeChange)
    document.removeEventListener("ui--flow:nodesadd", this._onNodesAdd)
    document.removeEventListener("ui--flow:edgesadd", this._onEdgeMembership)
    document.removeEventListener("ui--flow:edgedelete", this._onEdgeMembership)
    if (this._frameRaf) { cancelAnimationFrame(this._frameRaf); this._frameRaf = null; this._pending = false }
    if (this._labelRaf) { cancelAnimationFrame(this._labelRaf); this._labelRaf = null }
    // C4: drop the cached label state — a reconnected controller (element
    // re-attached elsewhere) rebuilds both from the fresh DOM at connect.
    this._labelMetrics = null
    this._parallelGroup = null
    this._flowController()?._deregisterEdgeElement?.(this.idValue, this.element)
    if (this._onEdgeKeyDown) this.element.removeEventListener("keydown", this._onEdgeKeyDown)
    if (this._retryTimer) clearTimeout(this._retryTimer)
    // Marker defs belong to this edge — remove them so deleted edges don't
    // leak <marker> elements into the shared <defs>.
    this._markers?.forEach(m => m.remove())
    if (this._interactionPath && this._onInteractionClick) this._interactionPath.removeEventListener("click", this._onInteractionClick)
    if (this._interactionPath && this._onInteractionEnter) {
      this._interactionPath.removeEventListener("mouseenter", this._onInteractionEnter)
      this._interactionPath.removeEventListener("mouseleave", this._onInteractionLeave)
    }
    this._interactionPath?.remove()
    this._labelGroup?.remove()
    if (this._sourceHandle) {
      this._sourceHandle.removeEventListener("pointerdown", this._onSourceDrag)
      this._sourceHandle.remove()
    }
    if (this._targetHandle) {
      this._targetHandle.removeEventListener("pointerdown", this._onTargetDrag)
      this._targetHandle.remove()
    }
  }

  // ── Value change callbacks ──

  // F11 (proper fix — supersedes flow_controller's updateEdge label poke):
  // _updateLabel builds the label group ONCE from the values at build time
  // and never re-reads them, so a live change to any label* value must drop
  // the rendered trio and refresh — the group is rebuilt from the new
  // values; an emptied label ("" → _updateLabel bails) simply stays removed.
  labelValueChanged()               { this._rebuildLabel() }
  labelShowBgValueChanged()         { this._rebuildLabel() }
  labelStyleValueChanged()          { this._rebuildLabel() }
  labelBgStyleValueChanged()        { this._rebuildLabel() }
  labelBgPaddingValueChanged()      { this._rebuildLabel() }
  labelBgBorderRadiusValueChanged() { this._rebuildLabel() }

  // Wave-8 A5: arrowhead colors are baked SVG presentation attributes, and
  // the flow-side marker gate only re-runs _setupMarkers for markerEnd/
  // markerStart — without this callback an updateEdge(id, {markerColor})
  // attribute write never repainted the arrowheads (stale until the next
  // select/deselect). _refreshMarkerColors no-ops when _markers is empty
  // (and pre-connect, when the value callback fires on hydration).
  markerColorValueChanged()         { if (this._markers) this._refreshMarkerColors() }

  // Wave-8 C2 invalidation: an endpoint rewire (reconnect completion rides
  // updateEdge's source/target attribute projection) changes WHICH element
  // an endpoint resolves to — the cached nested-anchors answer belongs to
  // the old pair and must recompute. The C4 parallel group is keyed by the
  // same source/target pair, so it refreshes here too. (_resolveEndpoint
  // also resets the anchors cache when a stale cached element is dropped;
  // connect() initializes both.) The port override belongs to the old value
  // as well — clear the rewire's side. The endpoint caches are also swept:
  // a resolution cached under a value that is no longer either current value
  // is stale (in particular a port-split one, cached under the full
  // "node.port" id with its override just deleted — without the sweep a
  // rewire back to that value would hit the cache and never re-record the
  // override). Rewires are rare; the sweep is trivial.
  sourceValueChanged() {
    this._nestedAnchors = null
    this._refreshParallelGroup()
    this._dropStaleEndpointCaches()
    if (this._portOverride) delete this._portOverride.source
  }
  targetValueChanged() {
    this._nestedAnchors = null
    this._refreshParallelGroup()
    this._dropStaleEndpointCaches()
    if (this._portOverride) delete this._portOverride.target
  }

  _dropStaleEndpointCaches() {
    if (!this._els) return
    for (const key of this._els.keys()) {
      if (key !== this.sourceValue && key !== this.targetValue) {
        this._els.delete(key)
        this._dims.delete(key)
        this._portSplit.delete(key)
      }
    }
  }

  _rebuildLabel() {
    // Stimulus fires value callbacks pre-connect on hydration (attribute
    // present) — connect() renders from the values as-is, so skip until the
    // controller's perf state exists.
    if (!this._els) return
    if (this._labelRaf) { cancelAnimationFrame(this._labelRaf); this._labelRaf = null }
    this._labelGroup?.remove()
    this._labelGroup = null
    this._labelText = null
    this._labelBg = null
    // C4: the measured box belongs to the dropped group — the rebuild
    // re-measures once (this is the "label values changed" re-measure path).
    this._labelMetrics = null
    this.refresh()
  }

  // Hidden overlays (UIUX-RF 7): the reconnect circles, interaction path and
  // label group are SVG SIBLINGS of the path in the edges layer, not
  // children — display:none on the path never touches them, so a hidden
  // edge left its updater circles floating at stale coordinates (verified
  // live: 14 orphaned circles on a search_filter canvas). Every hidden-state
  // writer funnels here: hide()/show() call it synchronously; this callback
  // covers attribute writes from updateEdge and host markup.
  hiddenValueChanged() { this._syncOverlayVisibility() }

  _syncOverlayVisibility() {
    const display = this.hiddenValue ? "none" : ""
    if (this._interactionPath) this._interactionPath.style.display = display
    if (this._sourceHandle) this._sourceHandle.style.display = display
    if (this._targetHandle) this._targetHandle.style.display = display
    if (this._labelGroup) this._labelGroup.style.display = display
  }

  _scheduleUpdate() {
    // One pending retry timer at a time (disconnect() assumes it): nodesadd
    // re-arms while a stale backoff timer may still be pending — cancel it so
    // the reschedule is literal instead of stacking a duplicate.
    if (this._retryTimer) { clearTimeout(this._retryTimer); this._retryTimer = null }
    this._retryCount++
    const delay = Math.min(this._retryCount * 30, 300)
    this._retryTimer = setTimeout(() => { this._retryTimer = null; this._updatePath() }, delay)
  }

  // Frame gate (perf): coalesce every nodemove/noderesize received between now
  // and the next animation frame into ONE _updatePath. During a multi-node
  // drag the engine dispatches per-node events synchronously; this flag folds
  // them into a single recompute per edge per frame.
  _scheduleFrameUpdate() {
    if (this._pending) return
    this._pending = true
    this._frameRaf = requestAnimationFrame(() => {
      this._pending = false
      this._frameRaf = null
      this._updatePath()
    })
  }

  // The edge element carries no ui--flow outlet (SVG sibling of the nodes
  // container), so resolve the engine ONCE through the enclosing canvas and
  // cache the answer — including the negative answer — for the whole lifetime.
  _flowController() {
    if (this._flowResolved) return this._flowControllerCache
    this._flowResolved = true
    this._flowControllerCache = null
    try {
      const canvas = this.element.closest("[data-controller~='ui--flow']")
      if (canvas && this.application) {
        this._flowControllerCache = this.application.getControllerForElementAndIdentifier(canvas, "ui--flow") || null
      }
    } catch (e) {
      this._flowControllerCache = null
    }
    return this._flowControllerCache
  }

  _findNodesContainer() {
    return this.element.closest("svg")?.parentElement?.querySelector("[data-ui--flow-target='nodes']") ||
           document.querySelector("[data-ui--flow-target='nodes']")
  }

  // Endpoint elements are cached per edge (the graph can be thousands of
  // edges; querySelector per _updatePath scales badly). Cache misses resolve
  // through the engine's element cache when a flow controller is reachable,
  // else fall back to the old container scan, then register the hit. A stale
  // cached element (removed outside the engine) is dropped and re-resolved.
  _resolveEndpoint(which) {
    const id = which === "source" ? this.sourceValue : this.targetValue
    let el = this._els.get(id)
    if (el) {
      if (el.isConnected) {
        // Cache-hit re-arm: the hit may serve a PREFIX node resolved through
        // the split under the full "node.port" id, whose override a rewire
        // (value-change hook) just deleted — restore it from the split
        // record. Plain ids (including legitimate dotted node ids) have no
        // record and stay clean.
        const port = this._portSplit?.get(id)
        if (port != null && this._portOverride) this._portOverride[which] = port
        return el
      }
      this._els.delete(id)
      this._dims.delete(id)
      this._portSplit?.delete(id)
      // A recreated endpoint element can be nested differently (remove+add
      // with a changed parentId) — the nested-anchors cache must recompute.
      this._nestedAnchors = null
    }
    const flow = this._flowController()
    el = null
    if (flow && typeof flow._nodeElement === "function") el = flow._nodeElement(id)
    if (!el) {
      const container = this._findNodesContainer()
      el = container?.querySelector(`[data-ui--flow-node="${CSS.escape(id)}"]`)
    }
    if (el) {
      // The full id resolved as-is — any port split this side picked up
      // earlier is stale (e.g. the "gate.vip" node materialized).
      if (this._portOverride) delete this._portOverride[which]
      this._portSplit?.delete(id)
      this._els.set(id, el)
      return el
    }
    // Port-qualified endpoint fallback (conek-workflows): the engine
    // addresses endpoints as "node.port" ("gate.vip" — node gate, port vip)
    // and the host emits those verbatim as the edge's source/target with no
    // separate handle value. When the full id MISSES as a node id AND the
    // id contains "." AND the prefix before the LAST "." resolves as a node,
    // anchor the endpoint to that node's declared port handle: record the
    // port suffix as this side's override so _updatePath feeds it to
    // _getHandlePosition in place of sourceHandleValue/targetHandleValue —
    // _resolveHandleSide then resolves the port handle's side exactly like
    // the existing semantic-handle path (keep in sync with
    // flow_controller._edgeEndpoint, which box-select consumes). All three
    // conditions are required; anything else behaves exactly as before
    // (retry → abandon).
    if (this._portOverride && typeof id === "string" && id.includes(".")) {
      const dot = id.lastIndexOf(".")
      const prefix = id.slice(0, dot)
      if (prefix) {
        let portEl = (flow && typeof flow._nodeElement === "function") ? flow._nodeElement(prefix) : null
        if (!portEl) {
          const container = this._findNodesContainer()
          portEl = container?.querySelector(`[data-ui--flow-node="${CSS.escape(prefix)}"]`) || null
        }
        if (portEl) {
          this._portOverride[which] = id.slice(dot + 1)
          this._portSplit?.set(id, id.slice(dot + 1))
          this._els.set(id, portEl)
          return portEl
        }
      }
    }
    return el
  }

  // ROW 9 (parent sub-flow drag): a parent drag carries its children's
  // ABSOLUTE positions — child style offsets are parent-relative (see
  // _flowPosition), and updateNodeDrag skips selected descendants because the
  // subtree follows via DOM nesting — while only the PARENT's nodemove is
  // emitted. If the moved node is a DOM ancestor of either endpoint, this
  // edge's anchors moved too and the path must re-project.
  //
  // Perf (wave-8 C2): the cached _hasNestedAnchors() short-circuit settles
  // FLAT graphs with two string compares — no endpoint is nested, so no
  // moved node can be an endpoint's ancestor. The old unconditional DOM
  // resolution cost O(selected × edges) per frame on a select-all drag
  // (~275k listener invocations on stress_500) recomputing a provably-false
  // answer. The resolved moved element is memoized ON THE EVENT
  // (one dispatch = one nodeId) so every edge listener shares one DOM
  // resolution per event instead of re-resolving per edge.
  _movedAncestorCarriesEndpoint(nodeId, event = null) {
    if (!this._hasNestedAnchors()) return false
    let moved = this._els.get(nodeId)
    if (!moved && event) moved = event.__flowMovedEl || null
    if (!moved) {
      const flow = this._flowController()
      if (flow && typeof flow._nodeElement === "function") moved = flow._nodeElement(nodeId)
    }
    if (!moved) {
      moved = this._findNodesContainer()?.querySelector(`[data-ui--flow-node="${CSS.escape(nodeId)}"]`) || null
    }
    if (event && moved && !event.__flowMovedEl) {
      try { Object.defineProperty(event, "__flowMovedEl", { value: moved, configurable: true }) } catch { /* non-extensible event — skip the memo */ }
    }
    if (!moved) return false
    const src = this._resolveEndpoint("source")
    const tgt = this._resolveEndpoint("target")
    return Boolean((src && moved.contains(src)) || (tgt && moved.contains(tgt)))
  }

  // Cached "an endpoint is DOM-nested inside another [data-ui--flow-node]"
  // (a sub-flow anchor). True only when a strict node ancestor exists —
  // the endpoint element IS itself a node, so the walk starts at its parent.
  _hasNestedAnchors() {
    if (this._nestedAnchors === null || this._nestedAnchors === undefined) {
      this._nestedAnchors = this._computeNestedAnchors()
    }
    return this._nestedAnchors
  }

  _computeNestedAnchors() {
    const nested = (el) => Boolean(el?.parentElement?.closest("[data-ui--flow-node]"))
    return nested(this._resolveEndpoint("source")) || nested(this._resolveEndpoint("target"))
  }

  // Warm the per-endpoint dims cache (see the nodemove listener for WHY this
  // runs in the event phase). Cache hits are two map gets; misses do exactly
  // the read _getHandlePosition would do later — same values, same geometry.
  _primeEndpointDims() {
    this._primeDimsFor("source")
    this._primeDimsFor("target")
  }

  _primeDimsFor(which) {
    const id = which === "source" ? this.sourceValue : this.targetValue
    if (this._dims.has(id)) return
    const el = this._resolveEndpoint(which)
    if (el) this._dims.set(id, { w: el.offsetWidth, h: el.offsetHeight })
  }

  // The NODE id an endpoint anchors to: the value itself, or — when the value
  // is port-qualified and the split fallback resolved it (see
  // _resolveEndpoint, which records the per-side _portOverride) — the prefix
  // before the last ".". The nodemove / noderesize / nodechange / nodesadd
  // filters compare THIS id against the event's nodeId, so a drag or resize
  // of the underlying node still recomputes a port-qualified edge's path
  // (the events carry the node id, the edge value is "node.port").
  _endpointNodeId(which) {
    const id = which === "source" ? this.sourceValue : this.targetValue
    return (this._portOverride?.[which] && typeof id === "string" && id.includes("."))
      ? id.slice(0, id.lastIndexOf("."))
      : id
  }

  // Evict a node's dims entry on noderesize/nodechange. _dims is keyed by
  // the FULL endpoint value (see _primeDimsFor / _getHandlePosition), so for
  // a port-qualified endpoint ("gate.vip") the event's node id alone is the
  // wrong key — deleting only "gate" would leave the full-value entry cached
  // and the recompute would short-circuit on the hit and serve the stale
  // box. Delete the node id AND, for each endpoint the node underlies, its
  // full value key.
  _evictDimsForEndpoint(nodeId) {
    this._dims.delete(nodeId)
    for (const which of ["source", "target"]) {
      if (this._endpointNodeId(which) === nodeId) {
        this._dims.delete(which === "source" ? this.sourceValue : this.targetValue)
      }
    }
  }

  // ═══════════ PATH UPDATE ═══════════

  _updatePath() {
    const sourceEl = this._resolveEndpoint("source")
    const targetEl = this._resolveEndpoint("target")
    if (!sourceEl || !targetEl) { if (this._retryCount < 10) this._scheduleUpdate(); return }
    // Endpoints resolved — the episode's backoff is discharged: a later
    // missing-endpoint outage starts from zero delay instead of inheriting
    // this episode's count (previously the count only ever climbed, so a
    // second outage with _retryCount ≥ 10 was abandoned on arrival).
    this._retryCount = 0

    // Port-qualified endpoints: the split fallback's per-side override
    // (recorded by _resolveEndpoint) replaces the handle value here, so the
    // endpoint anchors on the declared port handle's side exactly like the
    // semantic-handle path does. The dims cache keeps the FULL value as its
    // key on both sides — _primeDimsFor primes under the full value and
    // _getHandlePosition reads under it — so noderesize / nodechange evict
    // through _evictDimsForEndpoint, which deletes the full-value key (the
    // node id alone would be the wrong key and the stale box would survive
    // the recompute).
    const sp = this._getHandlePosition(sourceEl, this._portOverride?.source || this.sourceHandleValue, this.sourceValue)
    const tp = this._getHandlePosition(targetEl, this._portOverride?.target || this.targetHandleValue, this.targetValue)

    // Offset endpoints so the arrowhead clears the handle ring (UX audit):
    // refX=10 lands the tip exactly AT the path endpoint, which is also the
    // handle centre — the 10px handle dot (radius 5, z-index 20 above the
    // edges layer) then covers the tip-most ~5px of the 12.5px arrowhead
    // (6.25 × effective stroke-width 2). Retreating the endpoint by the
    // handle radius (5px) along the handle's outward normal puts the tip on
    // the ring's outer edge. Accepted drift, by design: the drag-time
    // connection preview (connection_line_controller) and box-select
    // (_edgeEndpoint) do NOT consume this offset, and an edge with
    // markerEnd="" gets no retreat while arrowed edges do (the knob is
    // conditional by design — the audit's fix intent is arrowhead
    // visibility). Selected edges bump the arrowhead to 15.625px — a fixed
    // 5px retreat still clears the 5px ring regardless.
    const MARKER_OFFSET = 5
    let sx = sp.x, sy = sp.y, tx = tp.x, ty = tp.y

    if (this.markerStartValue) {
      const off = this._getMarkerOffset(sp.position, MARKER_OFFSET)
      sx += off.x; sy += off.y
    }
    if (this.markerEndValue) {
      const off = this._getMarkerOffset(tp.position, MARKER_OFFSET)
      tx += off.x; ty += off.y
    }

    let result
    if (this.sourceValue === this.targetValue) {
      // Self-loop: both handles sit on one node, so a source→target path
      // collapses to zero length. Route a visible loop bulging out on the
      // source handle's side instead (bezier loop for every type). The loop
      // starts at sx,sy (markerStart offset included) and ends at tx,ty —
      // the markerEnd-retreated point — so the arrowhead clears the ring on
      // the return side too.
      result = this._getSelfLoopPath(sx, sy, tx, ty, sp.position)
    } else {
      switch (this.typeValue) {
        case "straight":
          result = this._getStraightPath(sx, sy, tx, ty)
          break
        case "step":
          result = this._getSmoothStepPath(sx, sy, sp.position, tx, ty, tp.position, 0)
          break
        case "smoothstep":
          result = this._getSmoothStepPath(sx, sy, sp.position, tx, ty, tp.position, this.borderRadiusValue)
          break
        default: // bezier
          result = this._getBezierPath(sx, sy, sp.position, tx, ty, tp.position)
          break
      }
    }

    const [path, labelX, labelY] = result

    // Stash the final geometry for the label positioning path: the parallel-
    // edge nudge reads the chord (target − source) for its perpendicular and
    // re-runs from the stored label point on edge membership changes.
    this._labelPoint = { x: labelX, y: labelY }
    this._labelChord = { x: tx - sx, y: ty - sy }

    this.element.setAttribute("d", path)
    if (this._interactionPath) this._interactionPath.setAttribute("d", path)
    this._updateLabel(labelX, labelY)
    this._updateReconnectHandlePositions()
  }

  // Returns the offset direction for pulling path endpoint back from the node edge,
  // so the arrowhead tip lands exactly on the handle, not inside the node.
  _getMarkerOffset(position, distance) {
    // Pull endpoint back so arrowhead tip lands exactly at the handle position.
    // "left" handle: edge arrives from the left, so pull endpoint left (−x).
    // "right" handle: edge arrives from the right, so pull endpoint right (+x).
    // "top" handle: edge arrives from above, so pull endpoint up (−y).
    // "bottom" handle: edge arrives from below, so pull endpoint down (+y).
    switch (position) {
      case "left":   return { x: -distance, y: 0 }
      case "right":  return { x:  distance, y: 0 }
      case "top":    return { x: 0, y: -distance }
      case "bottom": return { x: 0, y:  distance }
      default:       return { x:  distance, y: 0 }
    }
  }

  // Self-loop: a cubic bezier that leaves the source handle, bulges ~50px out
  // from the node edge on the handle's side, and returns to it. Control points
  // sit offset perpendicular (along the edge) and parallel (away from the node)
  // so the loop clears the node for every handle side. The loop END is the
  // markerEnd-retreated point (tx, ty) — the arrowhead's tip must clear the
  // handle ring on the return side too.
  _getSelfLoopPath(sx, sy, tx, ty, position) {
    const d = 50
    let c1x, c1y, c2x, c2y
    switch (position) {
      case "left":
        c1x = sx - d; c1y = sy - d; c2x = sx - d; c2y = sy + d
        break
      case "top":
        c1x = sx - d; c1y = sy - d; c2x = sx + d; c2y = sy - d
        break
      case "bottom":
        c1x = sx - d; c1y = sy + d; c2x = sx + d; c2y = sy + d
        break
      default: // right
        c1x = sx + d; c1y = sy - d; c2x = sx + d; c2y = sy + d
    }
    // Label (UX audit): the bezier t=0.5 apex sits ON the corridor any
    // through-edge leaving the same handle also uses (a right-side loop's
    // apex is exactly (sx + 37.5, sy)). Shift it 25px (d * 0.5) along the
    // unit vector apex → c1 so it lands up-and-out of the corridor; the
    // direction is generic across all four loop sides.
    let labelX = sx * 0.125 + c1x * 0.375 + c2x * 0.375 + tx * 0.125
    let labelY = sy * 0.125 + c1y * 0.375 + c2y * 0.375 + ty * 0.125
    const ux = c1x - labelX, uy = c1y - labelY
    const len = Math.hypot(ux, uy)
    if (len > 0) { labelX += (ux / len) * d * 0.5; labelY += (uy / len) * d * 0.5 }
    return [`M${sx},${sy} C${c1x},${c1y} ${c2x},${c2y} ${tx},${ty}`, labelX, labelY]
  }

  // Flow-space position of a node element. Sub-flow children (task 14) nest
  // inside their parent's DOM subtree, so style.left/top are RELATIVE to the
  // parent node — walk the node-ancestor chain summing offsets. Edges to/from
  // group children route to the child, not the group (spec §4.1). The edge
  // elements carry no ui--flow outlet, so this is a local copy of the flow
  // controller's flowPosition.
  _flowPosition(el) {
    let x = parseFloat(el.style.left) || 0, y = parseFloat(el.style.top) || 0
    let p = el.parentElement
    while (p && p.dataset && p.dataset["ui-FlowNode"]) {
      x += parseFloat(p.style.left) || 0
      y += parseFloat(p.style.top) || 0
      p = p.parentElement
    }
    return { x, y }
  }

  _getHandlePosition(el, pos, id = null) {
    const { x, y } = this._flowPosition(el)
    // Cached w/h (perf): offset reads force layout; the engine's noderesize
    // broadcast clears this per-edge cache. Keyed by node id so a recreated
    // endpoint element does not serve the old element's box.
    let dims = id ? this._dims.get(id) : null
    if (!dims) {
      dims = { w: el.offsetWidth, h: el.offsetHeight }
      if (id) this._dims.set(id, dims)
    }
    const handle = this._lookupHandle(el, pos)
    const side = this._resolveHandleSide(el, pos)
    // Multi-port (U9): a declared handle may sit part-way along its side.
    const anchor = sideAnchor(side, x, y, dims.w, dims.h, handleFraction(handle))
    return { ...anchor, position: ["top", "bottom", "left"].includes(side) ? side : "right" }
  }

  // Handle side for an endpoint (UX audit root cause B): the positional fast
  // path is verbatim; a NON-positional value is a semantic handle id
  // ("train-loop", "cache-loop") and resolves through the DECLARED handle
  // element's data-handle-position — the same resolution
  // flow_controller._edgeEndpoint makes (keep the two in sync): node-scoped
  // first (two nodes may carry the same handle id on different sides),
  // canvas-wide fallback for legacy markup that parks handles outside their
  // node, "right" when nothing resolves.
  _resolveHandleSide(el, pos) {
    if (pos === "top" || pos === "bottom" || pos === "left" || pos === "right") return pos
    return this._lookupHandle(el, pos)?.dataset.handlePosition || "right"
  }

  // The declared handle element behind a semantic (non-positional) handle
  // value, or null for a positional value / no match. Node-scoped first, then
  // canvas-wide (see _resolveHandleSide).
  _lookupHandle(el, pos) {
    if (pos === "top" || pos === "bottom" || pos === "left" || pos === "right") return null
    const raw = `${pos ?? ""}`
    const escaped = (typeof CSS !== "undefined" && typeof CSS.escape === "function")
      ? CSS.escape(raw)
      : raw.replace(/(["\\])/g, "\\$1")
    const selector = `[data-handle="${escaped}"]`
    const canvas = this.element.closest(".ui-flow-canvas")
    return el.querySelector(selector) || (canvas && canvas.querySelector(selector)) || null
  }

  // ═══════════ PATH SHAPES — delegated to lib/flow-edge-paths.js ═══════════
  // The exact React Flow ports live in the shared module (connection_line
  // controller consumes the same math for the drag preview); these wrappers
  // only inject the controller's own default values and keep the historical
  // [path, labelX, labelY] return shape for _updatePath / _getSelfLoopPath.

  _getBezierPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition) {
    return bezierPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, this.curvatureValue)
  }

  _getSmoothStepPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius) {
    return smoothStepPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius, this.offsetValue)
  }

  _getStraightPath(sourceX, sourceY, targetX, targetY) {
    return straightPath(sourceX, sourceY, targetX, targetY)
  }

  // ═══════════ INTERACTION PATH ═══════════

  _createInteractionPath() {
    const svg = this.element.closest("svg")
    if (!svg) return
    this._interactionPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
    this._interactionPath.setAttribute("fill", "none")
    this._interactionPath.setAttribute("stroke-opacity", "0")
    this._interactionPath.setAttribute("stroke-width", String(this.interactionWidthValue))
    this._interactionPath.setAttribute("stroke", "transparent")
    this._interactionPath.classList.add("ui-flow-edge-interaction")
    // `data-edge-interaction` is the blessed engine-internal attribute:
    // flow_controller's pointer handling reads it to select edges (and emits
    // `edgeclick` itself, so this controller must not dispatch it too).
    this._interactionPath.setAttribute("data-edge-interaction", this.idValue)
    this._onInteractionClick = (e) => {
      e.stopPropagation()
      if (!this.selectableValue) return
      this.select()
    }
    this._interactionPath.addEventListener("click", this._onInteractionClick)
    // Hover (spec gap closure ④): the interaction path is the hover surface
    // (the visible path is pointer-events:none). Enter/leave toggle the
    // `.hover` affordance class (CSS reads it) and dispatch on the edge
    // element so they bubble to the canvas root.
    this._onInteractionEnter = () => {
      this.element.classList.add("hover")
      this.element.dispatchEvent(new CustomEvent("ui--flow:edgeenter", { detail: { edgeId: this.idValue }, bubbles: true }))
    }
    this._onInteractionLeave = () => {
      this.element.classList.remove("hover")
      this.element.dispatchEvent(new CustomEvent("ui--flow:edgeleave", { detail: { edgeId: this.idValue }, bubbles: true }))
    }
    this._interactionPath.addEventListener("mouseenter", this._onInteractionEnter)
    this._interactionPath.addEventListener("mouseleave", this._onInteractionLeave)
    svg.insertBefore(this._interactionPath, this.element)
  }

  // ═══════════ RECONNECT HANDLES ═══════════

  _createReconnectHandles() {
    const svg = this.element.closest("svg")
    if (!svg) return

    const makeHandle = (type) => {
      const h = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      h.setAttribute("r", "8")
      h.setAttribute("fill", "transparent")
      h.setAttribute("stroke", "transparent")
      h.classList.add("ui-flow-edge-updater", `ui-flow-edge-updater-${type}`)
      return h
    }

    this._sourceHandle = makeHandle("source")
    this._targetHandle = makeHandle("target")

    // Set data attributes for test discoverability
    this._sourceHandle.setAttribute("data-edge-reconnect-source", this.idValue)
    this._targetHandle.setAttribute("data-edge-reconnect-target", this.idValue)

    this._onSourceDrag = (e) => {
      e.stopPropagation()
      this._dispatchReconnectStart({
        edgeId: this.idValue, type: "source",
        currentNode: this.sourceValue, currentHandle: this.sourceHandleValue
      })
    }
    this._onTargetDrag = (e) => {
      e.stopPropagation()
      this._dispatchReconnectStart({
        edgeId: this.idValue, type: "target",
        currentNode: this.targetValue, currentHandle: this.targetHandleValue
      })
    }

    this._sourceHandle.addEventListener("pointerdown", this._onSourceDrag)
    this._targetHandle.addEventListener("pointerdown", this._onTargetDrag)

    svg.appendChild(this._sourceHandle)
    svg.appendChild(this._targetHandle)
  }

  _updateReconnectHandlePositions() {
    if (!this._sourceHandle || !this._targetHandle) return
    const d = this.element.getAttribute("d")
    if (!d) return

    const startMatch = d.match(/M\s*([\d.-]+)[,\s]+([\d.-]+)/)
    if (startMatch) {
      this._sourceHandle.setAttribute("cx", startMatch[1])
      this._sourceHandle.setAttribute("cy", startMatch[2])
    }

    const coords = d.match(/([\d.-]+)[,\s]+([\d.-]+)/g)
    if (coords && coords.length >= 2) {
      const last = coords[coords.length - 1].split(/[,\s]+/)
      this._targetHandle.setAttribute("cx", last[0])
      this._targetHandle.setAttribute("cy", last[1])
    }
  }

  // ═══════════ LABEL — native SVG (React Flow style) ═══════════

  _updateLabel(labelX, labelY) {
    if (!this.labelValue) return
    const svg = this.element.closest("svg")
    if (!svg) return

    if (!this._labelGroup) {
      this._labelGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
      this._labelGroup.classList.add("ui-flow-edge-textwrapper")
      this._labelGroup.style.pointerEvents = "all"

      // Background rect
      this._labelBg = document.createElementNS("http://www.w3.org/2000/svg", "rect")
      this._labelBg.classList.add("ui-flow-edge-textbg")
      const [padX, padY] = this.labelBgPaddingValue
      this._labelBg.setAttribute("rx", String(this.labelBgBorderRadiusValue))
      this._labelBg.setAttribute("ry", String(this.labelBgBorderRadiusValue))
      if (this.labelShowBgValue) {
        this._labelGroup.appendChild(this._labelBg)
      }
      if (this.labelBgStyleValue && typeof this.labelBgStyleValue === "object") {
        Object.entries(this.labelBgStyleValue).forEach(([k, v]) => this._labelBg.style[k] = v)
      }

      // Text element
      this._labelText = document.createElementNS("http://www.w3.org/2000/svg", "text")
      this._labelText.classList.add("ui-flow-edge-text")
      this._labelText.setAttribute("dy", "0.3em")
      this._labelText.textContent = this.labelValue
      if (this.labelStyleValue && typeof this.labelStyleValue === "object") {
        Object.entries(this.labelStyleValue).forEach(([k, v]) => this._labelText.style[k] = v)
      }

      this._labelGroup.appendChild(this._labelText)
      svg.appendChild(this._labelGroup)
      // The group is an SVG sibling of the path — a hidden edge's label must
      // not float visible (UIUX-RF 7, same orphan class as the updaters).
      if (this.hiddenValue) this._labelGroup.style.display = "none"

      // Measure text after append — tracked so disconnect can cancel it (m3):
      // an untracked rAF outlives its controller and re-enters torn-down state.
      if (this._labelRaf) cancelAnimationFrame(this._labelRaf)
      this._labelRaf = requestAnimationFrame(() => {
        this._labelRaf = null
        this._measureAndPositionLabel(labelX, labelY)
      })
      return
    }

    this._measureAndPositionLabel(labelX, labelY)
  }

  _measureAndPositionLabel(labelX, labelY) {
    if (!this._labelText || !this._labelGroup) return

    // C4: measure ONCE per group build — getBBox() forces SVG layout, and
    // the text box only changes with the label values (which rebuild the
    // group via _rebuildLabel and null the cache). The per-frame drag path
    // reuses the cached box and only writes the translate.
    if (this._labelMetrics == null) {
      let bbox
      try {
        bbox = this._labelText.getBBox()
      } catch (e) {
        // SVG not rendered yet — retry on the next positioning pass.
        return
      }
      const [padX, padY] = this.labelBgPaddingValue
      this._labelMetrics = { width: bbox.width, height: bbox.height }

      // Geometry derived from the measured box is one-time too: the text
      // baseline and the background rect.
      this._labelText.setAttribute("y", String(bbox.height / 2))
      if (this._labelBg && this.labelShowBgValue) {
        this._labelBg.setAttribute("x", String(-padX))
        this._labelBg.setAttribute("y", String(-padY))
        this._labelBg.setAttribute("width", String(bbox.width + 2 * padX))
        this._labelBg.setAttribute("height", String(bbox.height + 2 * padY))
      }
    }
    const textWidth = this._labelMetrics.width
    const textHeight = this._labelMetrics.height

    // Parallel-edge nudge (UX audit): deterministic index-based offset so
    // same-pair edges stop stacking identical labels — transform only, the
    // path geometry is untouched.
    const nudge = this._parallelLabelOffset()

    // Position group centered on label point
    this._labelGroup.setAttribute("transform", `translate(${labelX + nudge.x - textWidth / 2} ${labelY + nudge.y - textHeight / 2})`)
  }

  // Re-run label positioning from the stored label point (edgesadd/
  // edgedelete re-order the parallel group) — no path rebuild.
  _repositionLabel() {
    if (!this._labelGroup || !this._labelPoint) return
    this._measureAndPositionLabel(this._labelPoint.x, this._labelPoint.y)
  }

  // Parallel-edge label nudge (UX audit): sibling edge paths in the same
  // edges-layer svg whose source/target form the same UNORDERED pair share a
  // group (a→b and b→a cross at the midpoint); the group index is the edge's
  // DOM position (stable: SSR renders all, runtime appends). With more than
  // one member, the label shifts (index − (count−1)/2) × 14px along the unit
  // perpendicular of the chord (see below for the orientation rule).
  // Single edges get no offset.
  //
  // C4: the group scan is CACHED (_refreshParallelGroup) — this method runs
  // per recompute frame and the old per-call querySelectorAll over EVERY
  // edge path in the svg made a labeled-edge drag O(deg × E) per frame.
  _parallelLabelOffset() {
    const zero = { x: 0, y: 0 }
    if (!this._parallelGroup) this._refreshParallelGroup()
    const group = this._parallelGroup
    if (!group || group.count <= 1 || group.index < 0) return zero
    const chord = this._labelChord || { x: 0, y: 0 }
    const len = Math.hypot(chord.x, chord.y)
    // The perpendicular is the chord rotated 90° counter-clockwise (−dy, dx),
    // normalized to point right-or-down (x > 0, or x === 0 and y > 0) so a
    // REVERSED edge's label does not land on its forward twin; a zero-length
    // chord (degenerate self-loop) falls back to straight up.
    let perp = len > 0 ? { x: -chord.y / len, y: chord.x / len } : { x: 0, y: -1 }
    if (perp.x < 0 || (perp.x === 0 && perp.y < 0)) perp = { x: -perp.x, y: -perp.y }
    const shift = (group.index - (group.count - 1) / 2) * 14
    return { x: perp.x * shift, y: perp.y * shift }
  }

  // The pair key shared by the group scan (space-joined SORTED pair —
  // commit e8b3ae7): a→b and b→a share a group.
  _pairKey(el) {
    return [el.getAttribute("data-ui--flow-edge-source-value"), el.getAttribute("data-ui--flow-edge-target-value")].sort().join(" ")
  }

  // Rebuild the cached {count, index} for this edge's parallel group from
  // the live svg. Called at connect, on edgesadd/edgedelete (the membership
  // hook), and on endpoint rewires (sourceValue/targetValue changes).
  _refreshParallelGroup() {
    const svg = this.element.closest("svg")
    if (!svg) { this._parallelGroup = null; return }
    const mine = this._pairKey(this.element)
    const group = [...svg.querySelectorAll("path[data-ui--flow-edge-source-value]")].filter(p => this._pairKey(p) === mine)
    this._parallelGroup = { count: group.length, index: group.indexOf(this.element) }
  }

  // ═══════════ MARKERS — React Flow style ═══════════

  _setupMarkers() {
    if (!this.markerStartValue && !this.markerEndValue) return
    const svg = this.element.closest("svg")
    if (!svg) return

    let defs = svg.querySelector("defs")
    if (!defs) {
      defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
      svg.insertBefore(defs, svg.firstChild)
    }

    if (this.markerEndValue) {
      const id = `ui-flow-marker-end-${this.idValue}`
      // F8: search the WHOLE svg, not just the first <defs> — EdgeComponent
      // renders each SSR edge's markers in a sibling <defs> after the path,
      // so a first-defs-only lookup misses them and hydration stamps a
      // duplicate marker with the same id for every edge after the first.
      let marker = svg.querySelector(`#${CSS.escape(id)}`)
      if (marker) {
        // SSR-pre-rendered def (EdgeComponent): bake currentColor to the
        // resolved color, which the baked markup cannot know.
        this._applyMarkerColor(marker, this._markerColor())
      } else {
        marker = this._createMarker(id, this.markerEndValue, this._markerColor(), "end")
        defs.appendChild(marker)
      }
      this._markers.push(marker)
      this.element.setAttribute("marker-end", `url(#${id})`)
    }
    if (this.markerStartValue) {
      const id = `ui-flow-marker-start-${this.idValue}`
      let marker = svg.querySelector(`#${CSS.escape(id)}`)
      if (marker) {
        this._applyMarkerColor(marker, this._markerColor())
      } else {
        marker = this._createMarker(id, this.markerStartValue, this._markerColor(), "start")
        defs.appendChild(marker)
      }
      this._markers.push(marker)
      this.element.setAttribute("marker-start", `url(#${id})`)
    }
  }

  // Resolved marker color: explicit value wins, else the edge element's
  // computed stroke (read at call time so selection state is reflected),
  // else the theme-token default. The computed stroke of an unstyled SVG path
  // is "none" (and jsdom returns "") — those are not colors and must fall
  // through to the token (m5).
  _markerColor() {
    if (this.markerColorValue) return this.markerColorValue
    const stroke = window.getComputedStyle(this.element).stroke
    if (stroke && stroke !== "none" && stroke !== "transparent") return stroke
    return "hsl(var(--muted-foreground))"
  }

  // Arrow markers are stroked outlines (fill=none); every other kind is a
  // filled shape whose stroke traces its own silhouette.
  _applyMarkerColor(marker, color) {
    const shape = marker.firstElementChild
    if (!shape) return
    if (shape.classList.contains("arrow")) {
      shape.setAttribute("stroke", color)
    } else {
      shape.setAttribute("fill", color)
      shape.setAttribute("stroke", color)
    }
  }

  // Re-resolve marker colors against the current edge state — selection
  // changes the edge stroke (CSS), so the arrowheads must follow.
  _refreshMarkerColors() {
    if (!this._markers.length) return
    const color = this._markerColor()
    this._markers.forEach(m => this._applyMarkerColor(m, color))
  }

  /**
   * Create SVG marker matching React Flow's MarkerSymbols.
   * Uses markerUnits="strokeWidth" so marker size scales with edge thickness;
   * 6.25 units at the CSS stroke-width of 2 renders a 12.5px arrowhead
   * (matching EdgeComponent's pre-rendered defs).
   * Marker registry kinds (marker_end/marker_start values):
   *   "arrow"      — open arrowhead (polyline, no fill)
   *   "arrowclosed" — filled arrowhead (path with fill; the default/fallback)
   *   "circle"     — filled circle
   *   "square"     — filled square
   * Unknown strings keep the historical fallback: the closed-arrow shape.
   *
   * The kind also lands on the marker's class as
   * `ui-flow-marker-{end,start}-{kind}` (spec §2.3), matching what
   * EdgeComponent pre-renders server-side.
   *
   * Colors are always resolved by _setupMarkers/_markerColor before calling
   * here — never "currentColor", which inside <defs> would track page text
   * color instead of the edge stroke.
   */
  _createMarker(id, type, color, position) {
    const m = document.createElementNS("http://www.w3.org/2000/svg", "marker")
    m.setAttribute("id", id)
    m.setAttribute("viewBox", "0 0 10 10")
    m.setAttribute("refX", "10")
    m.setAttribute("refY", "5")
    m.setAttribute("markerWidth", "6.25")
    m.setAttribute("markerHeight", "6.25")
    m.setAttribute("markerUnits", "strokeWidth")
    m.setAttribute("orient", "auto-start-reverse")
    m.classList.add("ui-flow-arrowhead", `ui-flow-marker-${position}-${type}`)

    // color is always resolved by _setupMarkers before calling here
    const c = color || "hsl(var(--muted-foreground))"

    switch (type) {
      case "arrow": {
        // Open arrow — chevron, no fill
        const p = document.createElementNS("http://www.w3.org/2000/svg", "polyline")
        p.setAttribute("points", "0,0 10,5 0,10")
        p.setAttribute("fill", "none")
        p.setAttribute("stroke", c)
        p.setAttribute("stroke-width", "1.5")
        p.setAttribute("stroke-linecap", "round")
        p.setAttribute("stroke-linejoin", "round")
        p.classList.add("arrow")
        m.appendChild(p)
        break
      }
      case "circle": {
        // Filled circle — geometry mirrors EdgeComponent#marker_shape
        const p = document.createElementNS("http://www.w3.org/2000/svg", "circle")
        p.setAttribute("cx", "5")
        p.setAttribute("cy", "5")
        p.setAttribute("r", "4")
        p.setAttribute("fill", c)
        p.setAttribute("stroke", c)
        p.setAttribute("stroke-width", "1")
        p.classList.add("circle")
        m.appendChild(p)
        break
      }
      case "square": {
        // Filled square — geometry mirrors EdgeComponent#marker_shape
        const p = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        p.setAttribute("x", "0")
        p.setAttribute("y", "0")
        p.setAttribute("width", "10")
        p.setAttribute("height", "10")
        p.setAttribute("fill", c)
        p.setAttribute("stroke", c)
        p.setAttribute("stroke-width", "1")
        p.classList.add("square")
        m.appendChild(p)
        break
      }
      default: {
        // arrowclosed — filled triangle
        const p = document.createElementNS("http://www.w3.org/2000/svg", "path")
        p.setAttribute("d", "M0,0 L10,5 L0,10 Z")
        p.setAttribute("fill", c)
        p.setAttribute("stroke", c)
        p.setAttribute("stroke-width", "1")
        p.setAttribute("stroke-linejoin", "round")
        p.classList.add("arrowclosed")
        m.appendChild(p)
      }
    }

    return m
  }

  // ═══════════ SELECTION / VISIBILITY ═══════════

  select() {
    if (!this.selectableValue) return
    // M2: route through the engine's selection model whenever a flow
    // controller is reachable (resolution here is try/catch-cached — see
    // _flowController). The engine's selectEdge owns the .selected class, the
    // selectedEdges set, edgeselect + selectionchange, the live-region
    // announcement, and clearSelection integration (pane click / Escape).
    // A local-only .selected was invisible to every one of those: the pane
    // could never clear it and the engine's Delete path never saw it. The
    // local block below survives ONLY as the no-engine fallback (standalone
    // mount, engine not yet connected). Marker recolor stays local — the
    // engine foregoes no rendering the edge controller owns.
    const flow = this._flowController()
    if (flow && typeof flow.selectEdge === "function") {
      flow.selectEdge(this.idValue)
      this._refreshMarkerColors()
      return
    }
    this.element.classList.add("selected")
    this._refreshMarkerColors()
    this._dispatch("edgeselect", { edgeId: this.idValue })
  }
  deselect() {
    // Engine-first mirror of select(); engines without a deselectEdge entry
    // point (or none reachable) fall through to the local class removal —
    // anything class-only here eventually syncs via clearSelection.
    const flow = this._flowController()
    if (flow && typeof flow.deselectEdge === "function") {
      flow.deselectEdge(this.idValue)
      this._refreshMarkerColors()
      return
    }
    this.element.classList.remove("selected")
    this._refreshMarkerColors()
    this._dispatch("edgedeselect", { edgeId: this.idValue })
  }
  // hide()/show() toggle the SVG-sibling overlays synchronously (updater
  // circles, interaction path, label group — UIUX-RF 7); the value write
  // also re-triggers the sync via hiddenValueChanged for attribute writers.
  hide()    { this.hiddenValue = true; this.element.style.display = "none"; this._syncOverlayVisibility() }
  show()    { this.hiddenValue = false; this.element.style.display = ""; this._syncOverlayVisibility() }
  refresh() { this._updatePath() }

  // "Edge from <source label or id> to <target label or id>" — nodes carry
  // their accessible name in aria-label (T21a contract); fall back to the id.
  _edgeAriaLabel() {
    const container = this._findNodesContainer()
    const label = (id) =>
      container?.querySelector(`[data-ui--flow-node="${CSS.escape(id)}"]`)?.getAttribute("aria-label") || id
    return `Edge from ${label(this.sourceValue)} to ${label(this.targetValue)}`
  }

  // S8: dispatch on the EDGE ELEMENT (bubbling), not document — the event
  // originates where React Flow parity expects (event.target = the edge),
  // and canvas-scoped listeners see it. Document-level listeners (the
  // engine's, host widgets') still receive it via bubbling.
  _dispatch(name, detail) {
    this.element.dispatchEvent(new CustomEvent(`ui--flow:${name}`, { detail, bubbles: true }))
  }

  // F7: reconnectstart goes out on the EDGE ELEMENT (bubbling), not document —
  // the event then carries a target inside the originating canvas, so canvas-
  // scoped listeners see it and the engine can reject another canvas's
  // gesture. Document-level listeners (flow_controller's today) still receive
  // it via bubbling.
  _dispatchReconnectStart(detail) {
    this.element.dispatchEvent(new CustomEvent("ui--flow:reconnectstart", { detail, bubbles: true }))
  }
}
